import type { Metadata } from "next"
import Link from "next/link"
import { notFound } from "next/navigation"
import {
  ArrowRight,
  Calculator,
  MessageCircle,
  ShieldCheck,
} from "lucide-react"

import { Container } from "@/components/layout/container"
import { Breadcrumbs } from "@/components/layout/breadcrumbs"
import { RecipeCard } from "@/components/recipes/recipe-card"
import { RecipeComments } from "@/components/recipes/recipe-comments"
import { RecipeHero } from "@/components/recipes/recipe-hero"
import { RecipeIngredients } from "@/components/recipes/recipe-ingredients"
import { RecipeRating } from "@/components/recipes/recipe-rating"
import { RecipeReviews } from "@/components/recipes/recipe-reviews"
import { RecipeSchema } from "@/components/recipes/recipe-schema"
import { RecipeSidebar } from "@/components/recipes/recipe-sidebar"
import { RecipeSteps } from "@/components/recipes/recipe-steps"
import { RecentlyViewedTracker } from "@/components/recipes/recently-viewed-tracker"
import { siteConfig } from "@/data/site"
import { getCurrentUser } from "@/lib/auth/get-user"
import { getApprovedCommentsByRecipeId } from "@/lib/comments"
import { isRecipeSavedByUser } from "@/lib/favorites"
import {
  getAllRecipeSlugs,
  getRecipeOrNull,
  getRelatedRecipes,
} from "@/lib/recipes"
import {
  getApprovedReviewsByRecipeId,
  getRatingSummaryByRecipeId,
} from "@/lib/reviews"
import { absoluteUrl } from "@/lib/utils"

type RecipePageProps = {
  params: {
    slug: string
  }
}

type HelpfulLink = {
  title: string
  text: string
  href: string
}

export const dynamicParams = false

function getHelpfulLinks(recipeCategory: string): HelpfulLink[] {
  if (recipeCategory === "fermentation") {
    return [
      {
        title: "Калькулятор солі та цукру",
        text: "Корисний для ферментації та точного підрахунку пропорцій.",
        href: "/calculators/salt-sugar",
      },
      {
        title: "Гайди з ферментації",
        text: "Пояснення, безпека та типові помилки для тих, хто хоче квасити впевненіше.",
        href: "/guides",
      },
      {
        title: "Усі рецепти",
        text: "Щоб порівняти цей варіант з іншими домашніми заготовками.",
        href: "/recipes",
      },
    ]
  }

  if (recipeCategory === "jam" || recipeCategory === "compote") {
    return [
      {
        title: "Калькулятори пропорцій",
        text: "Стануть у пригоді, якщо потрібно масштабувати рецепт або перевірити співвідношення інгредієнтів.",
        href: "/calculators",
      },
      {
        title: "Гайди та поради",
        text: "Пояснення про стерилізацію, зберігання та типові помилки під час домашніх заготовок.",
        href: "/guides",
      },
      {
        title: "Каталог рецептів",
        text: "Щоб подивитися ще більше варення, компотів та сезонних домашніх ідей.",
        href: "/recipes",
      },
    ]
  }

  return [
    {
      title: "Калькулятор маринаду",
      text: "Допомагає швидко перерахувати воду, сіль, цукор та оцет під іншу кількість банок.",
      href: "/calculators/marinade",
    },
    {
      title: "Конвертер оцту",
      text: "Корисний, якщо в рецепті вказана одна концентрація оцту, а у вас є інша.",
      href: "/calculators/vinegar-converter",
    },
    {
      title: "Гайди з безпеки",
      text: "Пояснення про стерилізацію, зберігання та типові помилки в домашній консервації.",
      href: "/guides",
    },
  ]
}

export function generateStaticParams() {
  return getAllRecipeSlugs().map((slug) => ({ slug }))
}

export async function generateMetadata({
  params,
}: RecipePageProps): Promise<Metadata> {
  const recipe = getRecipeOrNull(params.slug)

  if (!recipe) {
    return {
      title: "Рецепт не знайдено",
      robots: {
        index: false,
        follow: false,
      },
    }
  }

  const title = `${recipe.title} — рецепт ${siteConfig.name}`
  const description = recipe.description
  const canonicalPath = `/recipes/${recipe.slug}`
  const canonicalUrl = absoluteUrl(canonicalPath, siteConfig.url)
  const imageUrl = absoluteUrl(recipe.image, siteConfig.url)
  const publishedTime = recipe.publishedAt
    ? new Date(recipe.publishedAt).toISOString()
    : undefined
  const modifiedTime = recipe.updatedAt
    ? new Date(recipe.updatedAt).toISOString()
    : publishedTime

  return {
    title,
    description,
    authors: [{ name: siteConfig.creator }],
    creator: siteConfig.creator,
    publisher: siteConfig.organizationName,
    category: "food",
    alternates: {
      canonical: canonicalUrl,
    },
    robots: {
      index: true,
      follow: true,
    },
    openGraph: {
      title,
      description,
      type: "article",
      url: canonicalUrl,
      siteName: siteConfig.name,
      publishedTime,
      modifiedTime,
      authors: [siteConfig.creator],
      section: "Рецепти",
      images: [
        {
          url: imageUrl,
          width: 1200,
          height: 630,
          alt: recipe.title,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [imageUrl],
    },
  }
}

export default async function RecipePage({ params }: RecipePageProps) {
  const recipe = getRecipeOrNull(params.slug)

  if (!recipe) {
    notFound()
  }

  const user = await getCurrentUser()
  const relatedRecipes = getRelatedRecipes(recipe, 3)
  const helpfulLinks = getHelpfulLinks(recipe.category)

  const [initialReviews, ratingSummary, initialComments, isSaved] =
    await Promise.all([
      getApprovedReviewsByRecipeId(recipe.id),
      getRatingSummaryByRecipeId(recipe.id),
      getApprovedCommentsByRecipeId(recipe.id),
      user ? isRecipeSavedByUser(user.id, recipe.id) : Promise.resolve(false),
    ])

  return (
    <>
      <RecentlyViewedTracker recipeId={recipe.id} />

      <RecipeSchema
        recipe={recipe}
        reviews={initialReviews}
        ratingSummary={{
          average: ratingSummary.average,
          count: ratingSummary.count,
        }}
      />

      <section className="border-b border-neutral-200 bg-neutral-50/70 py-5">
        <Container>
          <Breadcrumbs
            items={[
              { label: "Головна", href: "/" },
              { label: "Рецепти", href: "/recipes" },
              { label: recipe.title },
            ]}
          />
        </Container>
      </section>

      <section className="py-8 sm:py-10">
        <Container>
          <RecipeHero recipe={recipe} />
        </Container>
      </section>

      <section className="pb-8 sm:pb-10">
        <Container className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
          <div className="space-y-6">
            <RecipeIngredients recipe={recipe} />
            <RecipeSteps recipe={recipe} />

            <RecipeRating
              average={ratingSummary.average}
              count={ratingSummary.count}
              distribution={ratingSummary.distribution}
            />

            <RecipeReviews
              recipeId={recipe.id}
              initialReviews={initialReviews}
            />

            <RecipeComments
              recipeId={recipe.id}
              initialComments={initialComments}
            />
          </div>

          <RecipeSidebar
            recipe={recipe}
            isSaved={isSaved}
            isAuthenticated={Boolean(user)}
          />
        </Container>
      </section>

      <section className="pb-12 sm:pb-14">
        <Container>
          <div className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-6 max-w-3xl">
              <div className="eyebrow">
                <Calculator className="mr-2 h-3.5 w-3.5" />
                Корисно поруч
              </div>

              <h2 className="mt-4 text-2xl font-semibold text-neutral-950 sm:text-3xl">
                Що може знадобитися після цього рецепта
              </h2>

              <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
                Рецепти на порталі працюють краще, коли поруч є інструменти,
                пояснення і наступні логічні кроки. Це допомагає готувати
                впевненіше, швидше знаходити потрібні рішення і не випадати з
                корисного маршруту по сайту.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {helpfulLinks.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5 transition-transform hover:-translate-y-0.5"
                >
                  <h3 className="text-base font-semibold text-neutral-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-neutral-600">
                    {item.text}
                  </p>

                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-emerald-700">
                    Перейти
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {relatedRecipes.length > 0 ? (
        <section className="border-t border-neutral-200 bg-neutral-50 py-12 sm:py-14">
          <Container>
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <div className="eyebrow">Схожі рецепти</div>

                <h2 className="mt-4 text-2xl font-semibold text-neutral-950 sm:text-3xl">
                  Спробуйте також
                </h2>

                <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
                  Якщо вам сподобався цей рецепт, зверніть увагу й на інші схожі
                  заготовки. Так простіше порівняти інгредієнти, способи
                  приготування й обрати варіант, який найкраще підходить саме
                  вам.
                </p>
              </div>

              <Link href="/recipes" className="button-secondary">
                Усі рецепти
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {relatedRecipes.map((relatedRecipe) => (
                <RecipeCard key={relatedRecipe.id} recipe={relatedRecipe} />
              ))}
            </div>
          </Container>
        </section>
      ) : null}

      <section className="py-12 sm:py-14">
        <Container>
          <div className="rounded-[2rem] border border-neutral-200 bg-gradient-to-br from-white to-emerald-50/50 p-6 shadow-sm sm:p-8">
            <div className="grid gap-6 lg:grid-cols-[1fr_0.95fr] lg:items-center">
              <div>
                <div className="eyebrow">
                  <MessageCircle className="mr-2 h-3.5 w-3.5" />
                  Корисно знати
                </div>

                <h2 className="mt-4 text-2xl font-semibold leading-tight text-neutral-950 sm:text-3xl">
                  Зберігайте рецепт, оцінюйте та діліться власним досвідом
                </h2>

                <p className="mt-4 text-sm leading-7 text-neutral-600 sm:text-base">
                  На сторінці рецепта можна швидко повернутися до інгредієнтів і
                  кроків, додати рецепт у збережені, залишити відгук або
                  поділитися своїми уточненнями в коментарях. Так каталог стає
                  кориснішим для всіх, хто готує вдома.
                </p>
              </div>

              <div className="rounded-[1.5rem] border border-white/80 bg-white/85 p-5">
                <div className="text-sm font-semibold text-neutral-950">
                  Що є на цій сторінці
                </div>

                <ul className="mt-3 space-y-3 text-sm leading-7 text-neutral-600">
                  <li>• список інгредієнтів з пропорціями</li>
                  <li>• покрокове приготування</li>
                  <li>• рейтинг і відгуки читачів</li>
                  <li>• коментарі та уточнення</li>
                  <li>• добірка схожих рецептів</li>
                  <li>• переходи до корисних інструментів і пояснень</li>
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="pb-16">
        <Container>
          <div className="rounded-[2rem] border border-amber-200 bg-amber-50/60 p-6 shadow-sm sm:p-8">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-1 h-5 w-5 text-amber-700" />

              <div>
                <h2 className="text-xl font-semibold text-neutral-950">
                  Важливо для безпеки
                </h2>

                <p className="mt-3 text-sm leading-7 text-neutral-700 sm:text-base">
                  Домашні заготовки завжди потребують уважності до чистоти,
                  пропорцій, стерилізації та умов зберігання. Якщо є сумніви
                  щодо зовнішнього вигляду, запаху або стану банки, безпека
                  важливіша за спробу зберегти продукт.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}